import { useState, useEffect } from 'react';
import Modal from '../shared/Modal';
import { getTeamLeads, getTeamRoles, saveTeamRoles, mergeTeams } from '../../utils/api';
import { usePassLevels } from '../../hooks/usePassLevels';

export default function EditTeamModal({ open, onClose, team, allTeams = [], onSave, onDelete, showToast, onRolesSaved, onMerged }) {
  const passLevels = usePassLevels();
  const [teamName, setTeamName]         = useState('');
  const [department, setDepartment]     = useState('Main');
  const [leadEmail, setLeadEmail]       = useState('');
  const [passLevel, setPassLevel]       = useState('');
  const [excludeMeals, setExcludeMeals] = useState(false);
  const [hideMasterMeals, setHideMasterMeals] = useState(false);
  const [excludeShuttles, setExcludeShuttles] = useState(false);
  const [groupBlocks, setGroupBlocks] = useState(false);
  const [staffLeads, setStaffLeads] = useState(false);
  const [kidsEnabled, setKidsEnabled]   = useState(false);
  const [teamLeads, setTeamLeads]       = useState([]);
  const [rolesEnabled, setRolesEnabled] = useState(false);
  const [roles, setRoles]               = useState([]);
  const [newRole, setNewRole]           = useState('');
  const [saving, setSaving]             = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [mergeTarget, setMergeTarget]   = useState('');
  const [confirmMerge, setConfirmMerge] = useState(false);
  const [merging, setMerging]           = useState(false);

  async function handleMerge() {
    if (!mergeTarget || merging) return;
    setMerging(true);
    try {
      const r = await mergeTeams(team.id, mergeTarget);
      showToast(`Merged: ${r?.merged || 'done'} — ${r?.moved?.roster ?? 0} people moved.`);
      setConfirmMerge(false);
      setMergeTarget('');
      if (onMerged) onMerged();
    } catch (err) {
      console.error('Merge failed:', err);
      showToast('Merge failed — the backend may need a redeploy.');
    } finally {
      setMerging(false);
    }
  }

  useEffect(() => {
    setConfirmDelete(false);
    setSaving(false);
    if (!open) return;
    getTeamLeads().then(d => setTeamLeads(d || [])).catch(console.error);
  }, [open]);

  useEffect(() => {
    if (!team) return;
    setTeamName(team.name || '');
    // Case/space-insensitive, matching every other department check — a sheet
    // cell holding "kids" must not silently demote the team to Main on save.
    setDepartment(String(team.department || '').trim().toLowerCase() === 'kids' ? 'Kids' : 'Main');
    setLeadEmail(team.leadEmail || '');
    setPassLevel(team.passLevel || 'SL');
    setExcludeMeals(team.excludeMeals === true || team.excludeMeals === 'true' || team.excludeMeals === 'TRUE');
    setHideMasterMeals(team.hideMasterMeals === true || team.hideMasterMeals === 'true' || team.hideMasterMeals === 'TRUE');
    setExcludeShuttles(team.excludeShuttles === true || team.excludeShuttles === 'true' || team.excludeShuttles === 'TRUE');
    setGroupBlocks(team.groupBlocks === true || team.groupBlocks === 'true' || team.groupBlocks === 'TRUE');
    setStaffLeads(team.staffLeads === true || team.staffLeads === 'true' || team.staffLeads === 'TRUE');
    setKidsEnabled(team.kidsEnabled === true || team.kidsEnabled === 'true' || team.kidsEnabled === 'TRUE');
  }, [team]);

  useEffect(() => {
    if (!open || !team?.id) return;
    getTeamRoles(team.id)
      .then(data => {
        setRolesEnabled(data?.enabled || false);
        setRoles(data?.roles || []);
      })
      .catch(console.error);
  }, [open, team?.id]);

  function addRole() {
    const trimmed = newRole.trim();
    if (!trimmed || roles.includes(trimmed)) return;
    setRoles(prev => [...prev, trimmed]);
    setNewRole('');
  }

  function removeRole(role) {
    setRoles(prev => prev.filter(r => r !== role));
  }

  async function handleSave() {
    if (!teamName.trim()) { showToast('Team name is required.'); return; }
    setSaving(true);
    // Parent (AdminTeams) updates state optimistically and handles API + errors
    onSave({
      ...team, name: teamName.trim(), department, leadEmail, passLevel,
      excludeMeals, excludeShuttles, groupBlocks, kidsEnabled, staffLeads, hideMasterMeals,
    });
    // Await roles save so we know it completed before closing
    try {
      await saveTeamRoles(team.id, rolesEnabled, roles);
      if (onRolesSaved) onRolesSaved(team.id, rolesEnabled, roles);
      onClose();
    } catch (err) {
      showToast('Error saving roles — please try again.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  function handleDelete() {
    onDelete(team.id);
    onClose();
  }

  if (!team) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Edit Team — ${team.name || ''}`}
      footer={
        <>
          {confirmDelete ? (
            <>
              <span style={{ fontFamily: 'var(--font-b)', fontSize: 13, fontWeight: 500, color: 'var(--black)' }}>Delete this team?</span>
              <button className="btn btn-sm" onClick={() => setConfirmDelete(false)}>Cancel</button>
              <button className="btn btn-sm btn-danger" onClick={handleDelete}>Yes, Delete</button>
            </>
          ) : (
            <button className="btn btn-danger btn-sm" onClick={() => setConfirmDelete(true)}>Delete Team</button>
          )}
          {!confirmDelete && <button className="btn" onClick={onClose}>Cancel</button>}
          {!confirmDelete && <button className="btn btn-dark" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</button>}
        </>
      }
    >
      <div className="form-group">
        <label className="form-label">Team Name</label>
        <input className="form-input" value={teamName} onChange={e => setTeamName(e.target.value)} />
      </div>
      <div className="form-group">
        <label className="form-label">Department</label>
        <select className="form-input" value={department} onChange={e => setDepartment(e.target.value)}>
          <option value="Main">Main</option>
          <option value="Kids">Kids</option>
        </select>
      </div>
      <div className="form-group">
        <label className="form-label">Team Lead</label>
        <select className="form-input" value={leadEmail} onChange={e => setLeadEmail(e.target.value)}>
          <option value="">None assigned</option>
          {teamLeads.map(tl => (
            <option key={tl.email} value={tl.email}>{tl.name || tl.email}</option>
          ))}
        </select>
      </div>
      <div className="form-group">
        <label className="form-label">Default Pass Level</label>
        <select className="form-input" value={passLevel} onChange={e => setPassLevel(e.target.value)}>
          {passLevels.map(pl => <option key={pl} value={pl}>{pl}</option>)}
        </select>
      </div>

      <div className="form-group">
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={excludeMeals}
            onChange={e => setExcludeMeals(e.target.checked)}
            style={{ accentColor: 'var(--black)', flexShrink: 0 }}
          />
          <div>
            <div style={{ fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: 700, color: 'var(--black)' }}>
              Exclude from meal blocks
            </div>
            <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)', marginTop: 2 }}>
              This team's roster won't count toward meal totals (e.g. teams that serve meals).
            </div>
          </div>
        </label>
      </div>

      <div className="form-group">
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={hideMasterMeals}
            onChange={e => setHideMasterMeals(e.target.checked)}
            style={{ accentColor: 'var(--black)', flexShrink: 0 }}
          />
          <div>
            <div style={{ fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: 700, color: 'var(--black)' }}>
              Hide master meals from team schedule
            </div>
            <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)', marginTop: 2 }}>
              <strong>Looks only</strong> — for teams whose lead enters their own meal times as team items
              (e.g. Worship's per-group dinners). The master schedule's meal rows stay off their team
              schedule; everyone still counts toward Kat's meal totals exactly the same.
            </div>
          </div>
        </label>
      </div>

      <div className="form-group">
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={excludeShuttles}
            onChange={e => setExcludeShuttles(e.target.checked)}
            style={{ accentColor: 'var(--black)', flexShrink: 0 }}
          />
          <div>
            <div style={{ fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: 700, color: 'var(--black)' }}>
              Exclude from shuttles
            </div>
            <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)', marginTop: 2 }}>
              Shuttle runs won't appear on this team's schedules (for teams that don't use transportation).
            </div>
          </div>
        </label>
      </div>

      <div className="form-group">
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={groupBlocks}
            onChange={e => setGroupBlocks(e.target.checked)}
            style={{ accentColor: 'var(--black)', flexShrink: 0 }}
          />
          <div>
            <div style={{ fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: 700, color: 'var(--black)' }}>
              Blocks are groups (no times)
            </div>
            <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)', marginTop: 2 }}>
              For teams like Worship: a block is just a named group of people with positions. Serving times come
              from team schedule items linked to the group — meals follow those times. Existing blocks keep their
              names and people.
            </div>
          </div>
        </label>
      </div>

      <div className="form-group">
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={staffLeads}
            onChange={e => setStaffLeads(e.target.checked)}
            style={{ accentColor: 'var(--black)', flexShrink: 0 }}
          />
          <div>
            <div style={{ fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: 700, color: 'var(--black)' }}>
              Staff / Leads
            </div>
            <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)', marginTop: 2 }}>
              Blocks work normally (regular date + times). This just unlocks an <strong>optional per-block
              checkbox</strong>: "all meals for a date range" — everyone assigned to that block gets
              <strong> every meal</strong> from the block date through the chosen end date, and they're
              included in meal counts. Blocks without the checkbox follow the normal meal rules.
            </div>
          </div>
        </label>
      </div>

      {department === 'Main' && (
        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={kidsEnabled}
              onChange={e => setKidsEnabled(e.target.checked)}
              style={{ accentColor: '#6AA292', flexShrink: 0 }}
            />
            <div>
              <div style={{ fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: 700, color: 'var(--black)' }}>
                Kids scheduling
              </div>
              <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)', marginTop: 2 }}>
                This team schedules people into Kids. Team leads get a "Kids" checkbox on each position; checked positions show up in the Kids Conference app.
              </div>
            </div>
          </label>
        </div>
      )}

      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 4 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 6 }}>
          <input
            type="checkbox"
            checked={rolesEnabled}
            onChange={e => setRolesEnabled(e.target.checked)}
            style={{ accentColor: 'var(--black)', flexShrink: 0 }}
          />
          <div>
            <div style={{ fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: 700, color: 'var(--black)' }}>
              Enable sub-roles for this team
            </div>
            <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)', marginTop: 2 }}>
              Team leads will see a separate roster tab per role in the sidebar.
            </div>
          </div>
        </label>

        {rolesEnabled && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 8 }}>Roles</div>
            <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden', marginBottom: 8 }}>
              {roles.length === 0 && (
                <div style={{ padding: '12px 14px', fontFamily: 'var(--font-b)', fontSize: 13, fontWeight: 300, color: 'var(--muted)' }}>
                  No roles yet — add one below.
                </div>
              )}
              {roles.map((role, i) => (
                <div key={role} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 14px', borderBottom: i < roles.length - 1 ? '1px solid var(--border)' : 'none', background: 'var(--off)' }}>
                  <span style={{ fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: 700, color: 'var(--black)' }}>{role}</span>
                  <button className="btn-ghost btn-xs" onClick={() => removeRole(role)} style={{ color: 'var(--danger, #c00)', fontSize: 12 }}>Remove</button>
                </div>
              ))}
              <div style={{ padding: 10, background: 'var(--off)', borderTop: roles.length > 0 ? '1px solid var(--border)' : 'none' }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    className="form-input"
                    placeholder="Role name, e.g. Concourse…"
                    value={newRole}
                    onChange={e => setNewRole(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addRole()}
                    style={{ flex: 1, fontSize: 13 }}
                  />
                  <button className="btn btn-sm btn-dark" onClick={addRole} disabled={!newRole.trim()}>Add</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="form-group" style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 8 }}>
        <label className="form-label">Merge this team into another</label>
        <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)', marginBottom: 8 }}>
          For duplicate rooms (e.g. "Film and Broadcast" vs "Broadcast and Film"): everyone on this
          team, plus its blocks and positions, moves to the team you pick — then this team is removed.
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            className="form-input"
            style={{ flex: 1, minWidth: 200 }}
            value={mergeTarget}
            onChange={e => { setMergeTarget(e.target.value); setConfirmMerge(false); }}
          >
            <option value="">— Choose the team to keep —</option>
            {(allTeams || []).filter(t => t.id !== team.id).map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          {mergeTarget && !confirmMerge && (
            <button className="btn btn-sm btn-danger" onClick={() => setConfirmMerge(true)}>Merge…</button>
          )}
          {mergeTarget && confirmMerge && (
            <>
              <span style={{ fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 500 }}>Move everyone + remove this team?</span>
              <button className="btn btn-xs" onClick={() => setConfirmMerge(false)} disabled={merging}>Cancel</button>
              <button className="btn btn-xs btn-danger" onClick={handleMerge} disabled={merging}>
                {merging ? 'Merging…' : 'Yes, Merge'}
              </button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
